import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  AdjustmentsHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  Bars3Icon,
  XMarkIcon as XMarkIconOutline,
  Cog6ToothIcon,
  CheckIcon,
  BookmarkIcon,
  XCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { Button, Badge, Checkbox } from "components/ui";
import { AdvancedFilters } from "./AdvancedFilters";
import { DatePicker } from "components/shared/form/Datepicker";
import {
  FilterDropdown,
  MultiSelectFilterDropdown,
  HierarchicalFilterDropdown,
  MultiSelectHierarchicalFilterDropdown,
} from "./filters";
import { useLocalStorage, useClickOutside, useUserPreference, PREFERENCE_TYPES } from "hooks";
import { getStageAndSubStages } from "../../services/leadStageService";
import { getDefaultVisibleStageIds, getAllStagesFilterPayload } from "../../utils/leadStageHelpers";
import { getTagsByCompanyId } from "../../services/leadTagsService";
import { getLeadsSources } from "../../services/leadsourcesServices";
import { getInternalUsersForAssigningLeads } from "../../services/userService";
import { getUsersForScheduleListFilter } from "../../services/jobScheduleService";
import { getJobGroups, getJobStates, getRolesWithUsers } from "services/jobsService";
import clsx from "clsx";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import "../../../src/styles/date-range-picker.css"; // custom styles to hide predefined ranges
import dayjs from "dayjs";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

const PRESET_DATE_RANGES = [
  {
    id: "last_month",
    label: "Last Month",
    getDates: () => ({
      fromDate: dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      toDate: dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
    }),
  },
  {
    id: "last_quarter",
    label: "Last Quarter",
    getDates: () => {
      const now = dayjs();
      const currentMonth = now.month(); // 0-indexed: Jan=0, Aug=7
      const currentYear = now.year();
      const currentQuarter = Math.floor(currentMonth / 3); // 0, 1, 2, 3

      let qStartMonth, qEndMonth, qYear;
      if (currentQuarter === 0) {
        qYear = currentYear - 1;
        qStartMonth = 9; // Oct
        qEndMonth = 11; // Dec
      } else {
        qYear = currentYear;
        qStartMonth = (currentQuarter - 1) * 3;
        qEndMonth = qStartMonth + 2;
      }

      const start = dayjs(new Date(qYear, qStartMonth, 1)).startOf("month").format("YYYY-MM-DD");
      const end = dayjs(new Date(qYear, qEndMonth, 1)).endOf("month").format("YYYY-MM-DD");

      return { fromDate: start, toDate: end };
    },
  },
  {
    id: "last_year",
    label: "Last Year",
    getDates: () => {
      const prevYear = dayjs().year() - 1;
      return {
        fromDate: `${prevYear}-01-01`,
        toDate: `${prevYear}-12-31`,
      };
    },
  },
  {
    id: "last_financial_year",
    label: "Last Financial Year",
    getDates: () => {
      const now = dayjs();
      const currentYear = now.year();
      const currentMonth = now.month(); // 0-indexed, July = 6
      const startYear = currentMonth >= 6 ? currentYear - 1 : currentYear - 2;
      const endYear = startYear + 1;
      return {
        fromDate: `${startYear}-07-01`,
        toDate: `${endYear}-06-30`,
      };
    },
  },
  {
    id: "this_month",
    label: "This Month",
    getDates: () => ({
      fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      toDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    }),
  },
  {
    id: "this_week",
    label: "This Week",
    getDates: () => ({
      fromDate: dayjs().startOf("week").format("YYYY-MM-DD"),
      toDate: dayjs().endOf("week").format("YYYY-MM-DD"),
    }),
  },
  {
    id: "today",
    label: "Today",
    getDates: () => ({
      fromDate: dayjs().format("YYYY-MM-DD"),
      toDate: dayjs().format("YYYY-MM-DD"),
    }),
  },
  {
    id: "yesterday",
    label: "Yesterday",
    getDates: () => ({
      fromDate: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
      toDate: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
    }),
  },
];

// DateRangePickerFilter component using preset select dropdown
function DateRangePickerFilter({
  filterKey,
  label,
  value,
  onChange,
  onClear,
  className,
  isOpen,
  onToggle,
}) {
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const DROPDOWN_WIDTH = Math.max(220, rect.width);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let left = rect.left;
      if (left + DROPDOWN_WIDTH > viewportWidth - 8) {
        left = Math.max(8, rect.right - DROPDOWN_WIDTH);
      }
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const DROPDOWN_HEIGHT = 280;
      
      let top = rect.bottom + 4;
      let bottom = "auto";
      let maxHeight = undefined;
      
      if (spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
        top = "auto";
        bottom = viewportHeight - rect.top + 4;
        maxHeight = spaceAbove - 10;
      } else {
        maxHeight = spaceBelow - 10;
      }
      
      setDropdownRect({
        top,
        bottom,
        left,
        width: rect.width,
        maxHeight,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      requestAnimationFrame(updatePosition);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, updatePosition]);

  useClickOutside(dropdownRef, (e) => {
    if (buttonRef.current && buttonRef.current.contains(e.target)) return;
    if (isOpen) onToggle(null);
  });

  const getActivePreset = () => {
    if (!value) return null;
    if (value.presetId) {
      const found = PRESET_DATE_RANGES.find((p) => p.id === value.presetId);
      if (found) return found;
    }
    if (!value.fromDate || !value.toDate) return null;

    const valStart = dayjs(value.fromDate).format("YYYY-MM-DD");
    const valEnd = dayjs(value.toDate).format("YYYY-MM-DD");

    return PRESET_DATE_RANGES.find((preset) => {
      const dates = preset.getDates();
      return dates.fromDate === valStart && dates.toDate === valEnd;
    }) || null;
  };

  const activePreset = getActivePreset();

  const displayValue = () => {
    if (activePreset) {
      return activePreset.label;
    }
    if (value?.fromDate && value?.toDate) {
      const start = dayjs(value.fromDate);
      const end = dayjs(value.toDate);
      if (start.year() === end.year()) {
        return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
      }
      return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
    }
    return "Select date range";
  };

  return (
    <div className={clsx("w-full", className)}>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(isOpen ? null : filterKey);
          }}
          className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-xs transition-all hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
        >
          <span
            className={clsx(
              "block truncate text-[11px] sm:text-xs tracking-tight",
              value?.fromDate && value?.toDate
                ? "font-medium text-gray-900 dark:text-gray-100"
                : "text-gray-500 dark:text-gray-400"
            )}
          >
            {displayValue()}
          </span>
          <div className="flex items-center gap-1.5 shrink-0 ml-1">
            {value?.fromDate && (
              <XCircleIcon
                className="h-4 w-4 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                  if (isOpen) {
                    onToggle(null);
                  }
                }}
              />
            )}
            <ChevronDownIcon
              className={clsx(
                "h-4 w-4 text-gray-400 transition-transform",
                isOpen && "rotate-180",
              )}
            />
          </div>
        </button>

        {isOpen &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[100000] min-w-[200px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-[#1f2937]"
              style={{
                top: dropdownRect?.top !== "auto" ? dropdownRect?.top : "auto",
                bottom: dropdownRect?.bottom !== "auto" ? dropdownRect?.bottom : "auto",
                left: dropdownRect?.left || 0,
                width: dropdownRect?.width ? `${Math.max(200, dropdownRect.width)}px` : 'auto',
                maxHeight: dropdownRect?.maxHeight ? `${dropdownRect.maxHeight}px` : '300px',
                overflowY: 'auto',
              }}
            >
              <div className="py-1">
                {PRESET_DATE_RANGES.map((preset) => {
                  const dates = preset.getDates();
                  const valStart = value?.fromDate ? dayjs(value.fromDate).format("YYYY-MM-DD") : null;
                  const valEnd = value?.toDate ? dayjs(value.toDate).format("YYYY-MM-DD") : null;
                  const isSelected = activePreset ? activePreset.id === preset.id : (valStart === dates.fromDate && valEnd === dates.toDate);
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => {
                        onChange({
                          ...dates,
                          presetId: preset.id,
                        });
                        onToggle(null);
                      }}
                      className={clsx(
                        "flex w-full items-center justify-between px-3.5 py-2.5 text-left text-xs font-medium transition-colors",
                        isSelected
                          ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 font-semibold"
                          : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700/60"
                      )}
                    >
                      <span>{preset.label}</span>
                      {isSelected && (
                        <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

// Reconstructs the flat list of selected leaf ids (stage id for a whole
// stage, sub-stage ids for a partially-selected stage) from a stageFilters
// array — shared by the read path and the default-selection comparison.
const flattenStageFilters = (stageFilters) => {
  if (!Array.isArray(stageFilters)) return [];
  const ids = [];
  stageFilters.forEach(({ stageId, subStageIds }) => {
    if (subStageIds && subStageIds.length > 0) {
      ids.push(...subStageIds);
    } else {
      ids.push(stageId);
    }
  });
  return [...new Set(ids)];
};

const idSetsEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const setB = new Set(b.map(String));
  return a.every((id) => setB.has(String(id)));
};

// Module-level cache for fetched data
let cachedStages = null;
let cachedTags = null;
let cachedReferralSources = null;
let cachedAssignees = null;
let cachedUsers = null;
let cachedGroups = null;
let cachedStates = null;
let cachedRolesWithUsers = null;
let stagesPromise = null;
let tagsPromise = null;
let referralSourcesPromise = null;
let assigneesPromise = null;
let usersPromise = null;
let groupsPromise = null;
let statesPromise = null;
let rolesWithUsersPromise = null;

// Eager load functions
const eagerLoadStages = () => {
  if (!stagesPromise) {
    stagesPromise = getStageAndSubStages({ pageSize: 100 })
      .then((data) => {
        cachedStages = data;
        return data;
      })
      .catch((err) => {
        console.error("Eager load stages error:", err);
        stagesPromise = null;
      });
  }
};

const eagerLoadTags = () => {
  if (!tagsPromise) {
    tagsPromise = getTagsByCompanyId(1, 1000, "", 1) // 1 = General (Lead tags)
      .then((result) => {
        if (result.success) {
          cachedTags = result.data;
          return result.data;
        }
        throw new Error(result.message || "Failed to fetch tags");
      })
      .catch((err) => {
        console.error("Eager load tags error:", err);
        tagsPromise = null;
      });
  }
};

const eagerLoadReferralSources = () => {
  if (!referralSourcesPromise) {
    referralSourcesPromise = getLeadsSources()
      .then((result) => {
        if (result.success) {
          cachedReferralSources = result.data;
          return result.data;
        }
        throw new Error(result.error || "Failed to fetch referral sources");
      })
      .catch((err) => {
        console.error("Eager load referral sources error:", err);
        referralSourcesPromise = null;
      });
  }
};

const eagerLoadAssignees = () => {
  if (!assigneesPromise) {
    assigneesPromise = getInternalUsersForAssigningLeads()
      .then((result) => {
        if (result.success) {
          cachedAssignees = result.data;
          return result.data;
        }
        throw new Error(result.error || "Failed to fetch internal users for assigning leads");
      })
      .catch((err) => {
        console.error("Eager load assignees error:", err);
        assigneesPromise = null;
      });
  }
};

const eagerLoadUsers = () => {
  if (!usersPromise) {
    usersPromise = getUsersForScheduleListFilter()
      .then((result) => {
        if (result.success) {
          cachedUsers = result.data;
          return result.data;
        }
        throw new Error(result.error || "Failed to fetch users");
      })
      .catch((err) => {
        console.error("Eager load users error:", err);
        usersPromise = null;
      });
  }
};

const eagerLoadGroups = () => {
  if (!groupsPromise) {
    groupsPromise = getJobGroups()
      .then((result) => {
        if (result.success) {
          const raw = result.data?.data || result.data || [];
          cachedGroups = Array.isArray(raw) ? raw : [];
          return cachedGroups;
        }
        throw new Error(result.message || "Failed to fetch job groups");
      })
      .catch((err) => {
        console.error("Eager load groups error:", err);
        groupsPromise = null;
      });
  }
};

const eagerLoadStates = () => {
  if (!statesPromise) {
    statesPromise = getJobStates()
      .then((result) => {
        if (result.success) {
          const raw = result.data?.data || result.data || [];
          cachedStates = Array.isArray(raw) ? raw : [];
          return cachedStates;
        }
        throw new Error(result.message || "Failed to fetch job states");
      })
      .catch((err) => {
        console.error("Eager load states error:", err);
        statesPromise = null;
      });
  }
};

const eagerLoadRolesWithUsers = () => {
  if (!rolesWithUsersPromise) {
    rolesWithUsersPromise = getRolesWithUsers()
      .then((result) => {
        if (result.success) {
          const raw = result.data?.data || result.data || [];
          cachedRolesWithUsers = Array.isArray(raw) ? raw : [];
          return cachedRolesWithUsers;
        }
        throw new Error(result.message || "Failed to fetch roles with users");
      })
      .catch((err) => {
        console.error("Eager load roles with users error:", err);
        rolesWithUsersPromise = null;
      });
  }
};

// Start eager loading immediately
// eagerLoadStages();
// eagerLoadTags();
// eagerLoadReferralSources();
// eagerLoadAssignees();
// eagerLoadUsers();
// eagerLoadGroups();
// eagerLoadStates();
// eagerLoadRolesWithUsers();

export const invalidateJobGroupsCache = () => {
  cachedGroups = null;
  groupsPromise = null;
  eagerLoadGroups();
};

export function LeadFilters({
  filters = {},
  onFilterChange,
  onClose,
  onCloseFilters,
  className,
  savedCount: initialSavedCount = 0,
  advancedFilterFields = [],
  supportedOperators = {},
  filterDropdowns = {},
  entityType = "lead",
}) {
  const [activeTab, setActiveTab] = useState("filter");
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [isQuickFiltersOpen, setIsQuickFiltersOpen] = useState(true);
  const [isRoleFiltersOpen, setIsRoleFiltersOpen] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [stages, setStages] = useState(cachedStages || []);
  const [loadingStages, setLoadingStages] = useState(!cachedStages);
  const [tags, setTags] = useState(cachedTags || []);
  const [loadingTags, setLoadingTags] = useState(!cachedTags);
  const [referralSources, setReferralSources] = useState(
    cachedReferralSources || [],
  );
  const [loadingReferralSources, setLoadingReferralSources] = useState(
    !cachedReferralSources,
  );
  const [assignees, setAssignees] = useState(cachedAssignees || []);
  const [loadingAssignees, setLoadingAssignees] = useState(!cachedAssignees);
  const [usersList, setUsersList] = useState(cachedUsers || []);
  const [loadingUsers, setLoadingUsers] = useState(!cachedUsers);
  const [groupsList, setGroupsList] = useState(cachedGroups || []);
  const [loadingGroups, setLoadingGroups] = useState(!cachedGroups);
  const [statesList, setStatesList] = useState(cachedStates || []);
  const [loadingStates, setLoadingStates] = useState(!cachedStates);
  const [rolesWithUsers, setRolesWithUsers] = useState(cachedRolesWithUsers || []);
  const [loadingRolesWithUsers, setLoadingRolesWithUsers] = useState(!cachedRolesWithUsers);
  const [savedCount, setSavedCount] = useState(initialSavedCount);

  // Local state to hold filter changes before they are applied
  const [localFilters, setLocalFilters] = useState(filters);

  // Translate job API keys back to UI keys for the local state
  const translateApiToUiFilters = useCallback((filtersObj) => {
    if (entityType !== "job") return filtersObj;
    const translated = { ...filtersObj };
    
    // Translate statusIds -> status
    if (filtersObj.statusIds !== undefined) {
      let parsedStatus = filtersObj.statusIds;
      if (typeof parsedStatus === "string") {
        try {
          let cleanStr = parsedStatus;
          if (cleanStr.startsWith("[") && cleanStr.endsWith("]")) {
            cleanStr = cleanStr.slice(1, -1);
          }
          parsedStatus = cleanStr ? cleanStr.split(",").map(Number) : [];
        } catch (e) {
          console.error("Failed to parse statusIds:", e);
          parsedStatus = [];
        }
      }
      translated.status = parsedStatus;
      delete translated.statusIds;
    }
    
    // Translate fromDate/toDate or modifiedFromDate/modifiedToDate -> dateRange
    if (filtersObj.fromDate || filtersObj.toDate || filtersObj.modifiedFromDate || filtersObj.modifiedToDate) {
      translated.dateRange = {
        fromDate: filtersObj.fromDate || filtersObj.modifiedFromDate,
        toDate: filtersObj.toDate || filtersObj.modifiedToDate,
      };
      delete translated.fromDate;
      delete translated.toDate;
      delete translated.modifiedFromDate;
      delete translated.modifiedToDate;
    }

    // Translate hideArchiveJobs -> hideAM
    if (filtersObj.hideArchiveJobs !== undefined) {
      translated.hideAM = filtersObj.hideArchiveJobs;
      delete translated.hideArchiveJobs;
    }

    // Translate jobUsers -> dynamic roleUser_* keys
    if (filtersObj.jobUsers !== undefined || filtersObj.JobUsers !== undefined || filtersObj.jobUserDesignations !== undefined) {
      let processedFromDesignations = false;
      
      // Support new jobUserDesignations payload mapping
      if (filtersObj.jobUserDesignations && Array.isArray(filtersObj.jobUserDesignations)) {
        filtersObj.jobUserDesignations.forEach((desig) => {
          const roleId = desig.designationId;
          const userId = desig.userId;
          if (roleId && userId) {
            const role = rolesWithUsers?.find(r => String(r.RoleId || r.roleId || r.DesignationId || r.designationId || r.Id || r.id) === String(roleId));
            const roleName = role?.designationName || "Role Users";
            const key = `roleUser_${roleId || roleName}`;
            
            if (!translated[key]) translated[key] = [];
            translated[key].push(String(userId).toLowerCase());
          }
        });
        processedFromDesignations = true;
      }

      let rawUsers = filtersObj.jobUsers || filtersObj.JobUsers;
      if (!Array.isArray(rawUsers)) {
        rawUsers = typeof rawUsers === "string" ? [rawUsers] : [];
      }
      const userIdsLower = rawUsers.map((u) => String(u).toLowerCase());

      if (!processedFromDesignations && rolesWithUsers && rolesWithUsers.length > 0) {
        rolesWithUsers.forEach((role) => {
          const roleId = role.RoleId || role.roleId || role.DesignationId || role.designationId || role.Id || role.id;
          const roleName = role.designationName || "Role Users";
          const key = `roleUser_${roleId || roleName}`;
          const roleUserList = role.users || role.Users || role.UserList || role.userList || [];

          const matchingForRole = roleUserList
            .map((u) => String(u.userId || u.UserId || u.id || u.Id || "").toLowerCase())
            .filter((uid) => uid && userIdsLower.includes(uid));

          if (matchingForRole.length > 0) {
            translated[key] = matchingForRole;
          }
        });
      } else if (userIdsLower.length > 0) {
        translated.users = userIdsLower;
      }
      
      delete translated.jobUsers;
      delete translated.JobUsers;
      delete translated.jobUserDesignations;
    }

    // Translate tradeSupplierUserIds back to roleUser_* for the Trade/Supplier dropdown
    if (filtersObj.tradeSupplierUserIds && Array.isArray(filtersObj.tradeSupplierUserIds) && filtersObj.tradeSupplierUserIds.length > 0) {
      if (rolesWithUsers && rolesWithUsers.length > 0) {
        const tradeRole = rolesWithUsers.find((r) => {
          const name = r.designationName || "";
          const n = name.toLowerCase().replace(/[\s/]+/g, "");
          return n === "tradesupplier" || n === "trade/supplier";
        });
        if (tradeRole) {
          const roleId = tradeRole.RoleId || tradeRole.roleId || tradeRole.DesignationId || tradeRole.designationId || tradeRole.Id || tradeRole.id;
          const key = `roleUser_${roleId}`;
          translated[key] = filtersObj.tradeSupplierUserIds.map((u) => String(u).toLowerCase());
        }
      }
      delete translated.tradeSupplierUserIds;
    }


    // Translate jobGroups / groupIds -> groups
    if (filtersObj.jobGroups !== undefined || filtersObj.groupIds !== undefined || filtersObj.groups !== undefined) {
      let rawGroups = filtersObj.jobGroups || filtersObj.groupIds || filtersObj.groups;
      if (typeof rawGroups === "string") {
        try { rawGroups = JSON.parse(rawGroups); } catch (e) { rawGroups = [rawGroups]; }
      }
      if (!Array.isArray(rawGroups)) {
        rawGroups = rawGroups ? [rawGroups] : [];
      }
      translated.groups = rawGroups.map(g => typeof g === "object" ? (g.GroupId || g.groupId || g.id || g.value) : String(g)).filter(Boolean);
      delete translated.jobGroups;
      delete translated.groupIds;
    }

    // Translate states -> states
    if (filtersObj.states !== undefined || filtersObj.state !== undefined) {
      let rawStates = filtersObj.states || filtersObj.state;
      if (typeof rawStates === "string") {
        try { rawStates = JSON.parse(rawStates); } catch (e) { rawStates = [rawStates]; }
      }
      if (!Array.isArray(rawStates)) {
        rawStates = rawStates ? [rawStates] : [];
      }
      translated.states = rawStates.map(s => typeof s === "object" ? (s.State || s.state || s.value || s.id) : String(s)).filter(Boolean);
      delete translated.state;
    }

    return translated;
  }, [entityType, rolesWithUsers]);

  // Sync local filters when global filters prop change or rolesWithUsers load
  useEffect(() => {
    let mergedFilters = { ...filters };
    if (filters.draftQuickFilters) {
      mergedFilters = { ...mergedFilters, ...filters.draftQuickFilters };
    }
    const translated = translateApiToUiFilters(mergedFilters);
    setLocalFilters(translated);

    // Auto-expand the role section if any role filter is active
    const hasActiveRoleFilter = Object.keys(translated).some((k) => {
      if (!k.startsWith("roleUser_") && k !== "users") return false;
      const v = translated[k];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
    if (hasActiveRoleFilter) setIsRoleFiltersOpen(true);
  }, [filters, rolesWithUsers, translateApiToUiFilters]);

  const mapUiFiltersToApiFilters = useCallback((filtersObj) => {
    if (entityType !== "job") return filtersObj;
    const mapped = { ...filtersObj };

    // Map status -> statusIds
    if (filtersObj.status !== undefined) {
      mapped.statusIds = Array.isArray(filtersObj.status) ? filtersObj.status.map(Number) : [];
      delete mapped.status;
    }

    // Map dateRange -> fromDate, toDate AND modifiedFromDate, modifiedToDate
    if (filtersObj.dateRange) {
      mapped.fromDate = filtersObj.dateRange.fromDate || null;
      mapped.toDate = filtersObj.dateRange.toDate || null;
      mapped.modifiedFromDate = filtersObj.dateRange.fromDate || null;
      mapped.modifiedToDate = filtersObj.dateRange.toDate || null;
      delete mapped.dateRange;
    } else {
      mapped.fromDate = null;
      mapped.toDate = null;
      mapped.modifiedFromDate = null;
      mapped.modifiedToDate = null;
    }

    // Map hideAM -> hideArchiveJobs
    if (filtersObj.hideAM !== undefined) {
      mapped.hideArchiveJobs = filtersObj.hideAM;
      delete mapped.hideAM;
    }

    // Collect all selected user IDs across dynamic roleUser_* dropdowns and legacy users key
    const selectedJobUsers = [];
    const jobUserDesignations = [];
    const tradeSupplierUserIds = [];

    // Helper: detect if a designation name is Trade/Supplier
    const isTradeSupplierDesignation = (name) => {
      if (!name) return false;
      const n = String(name).toLowerCase().replace(/[\s/]+/g, "");
      return n === "tradesupplier" || n === "trade/supplier" || n === "trade" || n === "supplier";
    };

    Object.keys(filtersObj).forEach((k) => {
      if (k.startsWith("roleUser_")) {
        const designationId = k.replace("roleUser_", "");
        const val = filtersObj[k];
        const usersForRole = Array.isArray(val) ? val : (val ? [val] : []);

        // Find the role entry to check its designation name
        const roleEntry = rolesWithUsers?.find((r) => {
          const rId = String(r.RoleId || r.roleId || r.DesignationId || r.designationId || r.Id || r.id || "");
          return rId === String(designationId);
        });
        const roleName = roleEntry?.designationName || "";

        if (isTradeSupplierDesignation(roleName)) {
          // Trade/Supplier users go into a dedicated list (matched against JobContractors)
          usersForRole.forEach((userId) => tradeSupplierUserIds.push(userId));
        } else {
          usersForRole.forEach((userId) => {
            jobUserDesignations.push({
              userId: userId,
              designationId: designationId,
            });
          });
        }
        delete mapped[k];
      } else if (k === "users") {
        const val = filtersObj[k];
        if (Array.isArray(val)) {
          selectedJobUsers.push(...val);
        } else if (val) {
          selectedJobUsers.push(val);
        }
        delete mapped[k];
      }
    });

    if (jobUserDesignations.length > 0) {
      mapped.jobUserDesignations = jobUserDesignations;
    } else {
      mapped.jobUserDesignations = null;
    }

    if (tradeSupplierUserIds.length > 0) {
      mapped.tradeSupplierUserIds = [...new Set(tradeSupplierUserIds)];
    } else if (filtersObj.tradeSupplierUserIds) {
      mapped.tradeSupplierUserIds = filtersObj.tradeSupplierUserIds;
    } else {
      mapped.tradeSupplierUserIds = null;
    }

    if (selectedJobUsers.length > 0) {
      mapped.jobUsers = [...new Set(selectedJobUsers)];
    } else if (filtersObj.jobUsers) {
      mapped.jobUsers = filtersObj.jobUsers;
    } else {
      mapped.jobUsers = null;
    }

    // Map groups -> jobGroups
    if (filtersObj.groups !== undefined) {
      mapped.jobGroups = filtersObj.groups;
      delete mapped.groups;
    }

    // Map states -> states (ensure array of strings and keep intact)
    if (filtersObj.states !== undefined) {
      let rawStates = filtersObj.states;
      if (!Array.isArray(rawStates)) {
        rawStates = typeof rawStates === "string" ? [rawStates] : [];
      }
      mapped.states = rawStates
        .map((s) => (typeof s === "object" ? (s.State || s.state || s.value || s.id) : String(s)))
        .filter(Boolean);
    }

    return mapped;
  }, [entityType, rolesWithUsers]);

  // Dynamic predefined quick filters configuration
  const PREDEFINED_QUICK_FILTERS = useMemo(() => {
    if (entityType !== "job") {
      return [
        {
          key: "referralSourceIds",
          label: "Referral Source",
          inputType: "multi-select",
          visible: true,
          order: 0,
        },
        {
          key: "stageSubstage",
          label: "Stage / Sub Stage",
          inputType: "multi-select-hierarchy",
          visible: true,
          order: 1,
        },
        {
          key: "leadScore",
          label: "Lead Score",
          inputType: "multi-select",
          visible: true,
          order: 2,
        },
        {
          key: "interactionLevel",
          label: "Interaction Level",
          inputType: "multi-select",
          visible: true,
          order: 3,
        },
        {
          key: "assignedTo",
          label: "Assigned To",
          inputType: "multi-select",
          visible: true,
          order: 4,
        },
        {
          key: "tags",
          label: "Tags",
          inputType: "multi-select",
          visible: true,
          order: 5,
        },
        {
          key: "priority",
          label: "Priority",
          inputType: "multi-select",
          visible: true,
          order: 6,
        },
        {
          key: "dateRange",
          label: "Date Range",
          inputType: "date-range",
          visible: true,
          order: 7,
        },
      ];
    }

    const jobFiltersList = [
      {
        key: "status",
        label: "Job Status",
        inputType: "multi-select",
        visible: true,
        order: 0,
      },
      {
        key: "dateRange",
        label: "Date Range",
        inputType: "date-range",
        visible: true,
        order: 1,
      },
      {
        key: "groups",
        label: "Group",
        inputType: "multi-select",
        visible: true,
        order: 2,
      },
      {
        key: "states",
        label: "Location / State",
        inputType: "multi-select",
        visible: true,
        order: 3,
      },
    ];

    let currentOrder = 4;
    if (rolesWithUsers && rolesWithUsers.length > 0) {
      rolesWithUsers.forEach((role) => {
        const roleId = role.RoleId || role.roleId || role.DesignationId || role.designationId || role.Id || role.id;
        const roleName = role.designationName || "Role Users";
        const users = role.Users || role.users || role.UserList || role.userList || [];
        jobFiltersList.push({
          key: `roleUser_${roleId || roleName}`,
          label: roleName,
          inputType: "multi-select",
          visible: true,
          order: currentOrder++,
          users: users,
        });
      });
    } else {
      jobFiltersList.push({
        key: "users",
        label: "Users",
        inputType: "multi-select",
        visible: true,
        order: currentOrder++,
      });
    }

    return jobFiltersList;
  }, [entityType, rolesWithUsers]);

  // Generate filter configuration with predefined filters first
  const generateDefaultFilterConfig = () => {
    // Start with predefined filters
    const predefinedConfig = [...PREDEFINED_QUICK_FILTERS];

    // Add remaining advanced filter fields that are not in predefined list
    if (advancedFilterFields && advancedFilterFields.length > 0) {
      const predefinedKeys = new Set(
        PREDEFINED_QUICK_FILTERS.map((f) => f.key),
      );
      let additionalOrder = PREDEFINED_QUICK_FILTERS.length;

      advancedFilterFields.forEach((field) => {
        if (
          !predefinedKeys.has(field.fieldName) &&
          !["leadSourceId", "LeadSourceFormId"].includes(field.fieldName)
        ) {
          predefinedConfig.push({
            key: field.fieldName,
            label: field.displayName,
            visible: false, // Additional fields hidden by default
            order: additionalOrder++,
          });
        }
      });
    }

    return predefinedConfig;
  };

  const filterPageKey = `${entityType || "opportunities"}_list`;

  const { preferenceState: apiFilterConfig, savePreference: saveApiFilterConfig } = useUserPreference({
    pageKey: filterPageKey,
    typeKey: "FilterConfig",
    defaultState: null,
  });

  const [localFilterConfigState, setLocalFilterConfigState] = useState(() => generateDefaultFilterConfig() || []);
  const filterConfig = (apiFilterConfig && Array.isArray(apiFilterConfig) && apiFilterConfig.length > 0)
    ? apiFilterConfig
    : localFilterConfigState;

  const setFilterConfig = (updater) => {
    const nextConfig = typeof updater === "function" ? updater(filterConfig) : updater;
    saveApiFilterConfig(nextConfig);
    setLocalFilterConfigState(nextConfig);
  };


  // Update filter config when advancedFilterFields change
  useEffect(() => {
    if (advancedFilterFields && advancedFilterFields.length > 0) {
      const newConfig = generateDefaultFilterConfig();
      if (newConfig && Array.isArray(newConfig)) {
        setFilterConfig(newConfig);
      }
    }
  }, [advancedFilterFields]);

  // Cleanup effect to remove stale/removed filters from localStorage
  useEffect(() => {
    if (filterConfig && Array.isArray(filterConfig)) {
      const staleKeys = ["leadSourceId", "LeadSourceFormId", "hideAM"];
      const hasStale = filterConfig.some((c) => c?.key && staleKeys.includes(c.key));
      if (hasStale) {
        setFilterConfig((prev) =>
          (prev || []).filter((c) => c?.key && !staleKeys.includes(c.key)),
        );
      }
    }
  }, [filterConfig]);

  // Migration effect to enforce new layout if old order is stored in localStorage
  useEffect(() => {
    if (filterConfig && Array.isArray(filterConfig)) {
      const dateRangeConfig = filterConfig.find(c => c?.key === "dateRange");
      if (dateRangeConfig && dateRangeConfig.order === 0 && entityType === "lead") {
        const newDefaultConfig = generateDefaultFilterConfig();
        if (newDefaultConfig) {
          setFilterConfig(newDefaultConfig);
        }
      }
    }
  }, [filterConfig, entityType]);

  // Sync filterConfig with PREDEFINED_QUICK_FILTERS to ensure dynamic filters (like roleUser_*) are added
  useEffect(() => {
    setFilterConfig((prev) => {
      const currentConfig = (prev || []).filter((c) => c && c.key);
      const currentKeys = new Set(currentConfig.map((c) => c.key));
      let changed = false;
      const newConfig = [...currentConfig];

      PREDEFINED_QUICK_FILTERS.forEach((pf) => {
        if (pf?.key && !currentKeys.has(pf.key)) {
          newConfig.push({
            key: pf.key,
            label: pf.label,
            visible: pf.visible,
            order: pf.order,
          });
          changed = true;
        }
      });

      // Remove dynamic filters that are no longer valid (e.g. switching from 'users' to 'roleUser_*')
      const validPredefinedKeys = new Set(
        PREDEFINED_QUICK_FILTERS.filter((f) => f && f.key).map((f) => f.key)
      );
      const filteredConfig = newConfig.filter((c) => {
        if (!c || !c.key) return false;
        if (typeof c.key === "string" && (c.key.startsWith("roleUser_") || c.key === "users")) {
          if (!validPredefinedKeys.has(c.key)) {
            changed = true;
            return false;
          }
        }
        return true;
      });

      if (changed) {
        return filteredConfig.sort((a, b) => {
          const orderA = PREDEFINED_QUICK_FILTERS.find((p) => p?.key === a?.key)?.order ?? a?.order ?? 0;
          const orderB = PREDEFINED_QUICK_FILTERS.find((p) => p?.key === b?.key)?.order ?? b?.order ?? 0;
          return orderA - orderB;
        });
      }

      return prev;
    });
  }, [PREDEFINED_QUICK_FILTERS, setFilterConfig]);

  // Ids that appear pre-checked in the Stage/Sub Stage filter until the user
  // makes an explicit choice — everything except Lost/job stages, mirroring
  // what fn_FilterLeads.sql hides by default when no stageFilters are sent.
  const defaultStageOrSubstageIds = useMemo(
    () => getDefaultVisibleStageIds(stages),
    [stages],
  );

  // Explicit payload for the "All" option — every stage, including the ones
  // hidden by default.
  const allStagesFilterPayload = useMemo(
    () => getAllStagesFilterPayload(stages),
    [stages],
  );

  const handleFilterSelect = (filterKey, value, type) => {
    // Special handling for hierarchical stageSubstage filter
    if (filterKey === "stageSubstage") {
      const newFilters = { ...localFilters, appliedSavedFilters: [] };

      // Clear old flat fields and the new stageFilters field
      delete newFilters.stageId;
      delete newFilters.subStageId;
      delete newFilters.stageFilters;

      if (type === "all") {
        // Explicitly select every stage, including the ones hidden by
        // default (Lost, job stages) — a deliberate override, so it's
        // stored explicitly rather than left to fall back to the default.
        if (allStagesFilterPayload.length > 0) {
          newFilters.stageFilters = allStagesFilterPayload;
        }
      } else if (Array.isArray(value) && value.length > 0) {
        // Build a map of stageId -> Set of subStageIds selected
        const stageMap = new Map(); // stageId -> Set<subStageId> | null

        value.forEach((selectedId) => {
          const stage = stages.find((s) => s.stageId === selectedId);
          if (stage) {
            // A top-level stage was selected
            if (!stageMap.has(stage.stageId)) {
              // Only set to null if no substages were explicitly selected yet
              stageMap.set(stage.stageId, stageMap.get(stage.stageId) ?? null);
            }
          } else {
            // It's a substage — find its parent
            const parentStage = stages.find(
              (s) =>
                s.subStages?.some((sub) => sub.subStageId === selectedId) ||
                s.SubStages?.some((sub) => sub.subStageId === selectedId),
            );
            if (parentStage) {
              const existing = stageMap.get(parentStage.stageId);
              if (existing === null || existing === undefined) {
                stageMap.set(parentStage.stageId, new Set([selectedId]));
              } else {
                existing.add(selectedId);
              }
            }
          }
        });

        // Convert map to the API payload structure
        const stageFilters = Array.from(stageMap.entries()).map(
          ([stageId, subSet]) => ({
            stageId,
            subStageIds: subSet ? Array.from(subSet) : null,
          }),
        );

        // If this selection is exactly the default (everything except
        // Lost/job stages), leave stageFilters unset so it stays uncounted
        // as a filter and matches the backend's own default when nothing
        // is sent.
        if (
          stageFilters.length > 0 &&
          !idSetsEqual(flattenStageFilters(stageFilters), defaultStageOrSubstageIds)
        ) {
          newFilters.stageFilters = stageFilters;
        }
      } else if (type && value && !Array.isArray(value)) {
        // Handle single selection (backward compatibility)
        if (type === "stage") {
          newFilters.stageFilters = [{ stageId: value, subStageIds: null }];
        } else if (type === "substage") {
          const parentStage = stages.find(
            (stage) =>
              stage.subStages?.some((sub) => sub.subStageId === value) ||
              stage.SubStages?.some((sub) => sub.subStageId === value),
          );
          if (parentStage) {
            newFilters.stageFilters = [
              { stageId: parentStage.stageId, subStageIds: [value] },
            ];
          }
        }
      }

      setLocalFilters(newFilters);
    } else {
      let finalValue = value;
      const newFilters = { ...localFilters, [filterKey]: finalValue, appliedSavedFilters: [] };
      setLocalFilters(newFilters);

      // Instantly apply when unchecking the "Hide Archive Jobs" filter
      if (filterKey === "hideAM" && finalValue === false) {
        const apiFilters = mapUiFiltersToApiFilters(newFilters);
        onFilterChange?.(apiFilters);
      }
    }
  };

  const handleClearFilter = (filterKey) => {
    const newFilters = { ...localFilters, appliedSavedFilters: [] };
    const translatedAppliedFilters = translateApiToUiFilters(filters);
    let wasApplied = false;

    // Special handling for hierarchical stageSubstage filter
    if (filterKey === "stageSubstage") {
      if (
        translatedAppliedFilters.stageId ||
        translatedAppliedFilters.subStageId ||
        (Array.isArray(translatedAppliedFilters.stageFilters) && translatedAppliedFilters.stageFilters.length > 0)
      ) {
        wasApplied = true;
      }
      delete newFilters.stageId;
      delete newFilters.subStageId;
      delete newFilters.stageFilters;
    } else {
      const val = translatedAppliedFilters[filterKey];
      if (Array.isArray(val) ? val.length > 0 : !!val) {
        wasApplied = true;
      }
      delete newFilters[filterKey];
    }

    setLocalFilters(newFilters);

    if (wasApplied) {
      const apiFilters = mapUiFiltersToApiFilters(newFilters);
      onFilterChange?.(apiFilters);
    }
  };

  const getActiveFiltersCount = () => {
    return Object.keys(localFilters).filter(
      (key) => {
        if (key === 'leadview' || key === 'search' || key === 'applicantTab' || key === 'selectedColumns') return false;
        if (key === 'groupedFilters' || key === 'appliedSavedFilters') { return localFilters[key]?.Groups?.length > 0; }
        const value = localFilters[key];
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'boolean') {
          return value;
        }
        return value && value !== "all";
       
    }).length;
  };

  // Create filter definitions with predefined filters and API data
  const createFilterDefinitionsMap = useCallback(() => {
    const definitionsMap = {};

    // Only add predefined filters - no extra ones
    PREDEFINED_QUICK_FILTERS.forEach((predefinedFilter) => {
      let options = [];
      let inputType = predefinedFilter.inputType;

      // Special handling for hierarchical stageSubstage filter
      if (predefinedFilter.key === "stageSubstage") {
        // Transform stages data into hierarchical options format
        options = stages.map((stage) => ({
          id: stage.stageId,
          value: stage.stageId,
          label: stage.stageName,
          type: "stage",
          children: (stage.subStages || stage.SubStages || []).map(
            (subStage) => ({
              id: subStage.subStageId,
              value: subStage.subStageId,
              label: subStage.subStageName,
              type: "substage",
              parentId: stage.stageId,
            }),
          ),
        }));
        inputType = "multi-select-hierarchy";
      } else if (predefinedFilter.key === "leadScore") {
        // Precise tiered score ranges as per requirements
        options = [
          {
            id: "0|15",
            min: 0,
            max: 15,
            value: "0|15",
            label: "Very Low / Cold (0 - 15)",
          },
          {
            id: "15|30",
            min: 15,
            max: 30,
            value: "15|30",
            label: "Low (15 - 30)",
          },
          {
            id: "30|45",
            min: 30,
            max: 45,
            value: "30|45",
            label: "Below Average (30 - 45)",
          },
          {
            id: "45|60",
            min: 45,
            max: 60,
            value: "45|60",
            label: "Average (45 - 60)",
          },
          {
            id: "60|90",
            min: 60,
            max: 90,
            value: "60|90",
            label: "Good to Very Good (60 - 90)",
          },
          {
            id: "91|",
            min: 91,
            max: null,
            value: "91|",
            label: "Hot / Qualified (Above 90)",
          },
        ];
        inputType = "multi-select";
      } else if (predefinedFilter.key === "assignedTo") {
        if (assignees && assignees.length > 0) {
          options = assignees.map((user) => {
            const uid = String(user.userId || user.value || user.id || "").toLowerCase();
            const designationsStr = Array.isArray(user.designations) && user.designations.length > 0 
                ? user.designations.map(d => d.DesignationName || d.designationName).filter(Boolean).join(", ")
                : (user.designationName || user.DesignationName || user.roleName || user.RoleName || "");
            return {
              id: uid,
              value: uid,
              label: user.fullName || user.label || user.name || "",
              badgeText: designationsStr,
            };
          });
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key === "users") {
        if (usersList && usersList.length > 0) {
          options = usersList.map((user) => {
            const uid = String(user.UserId || user.userId || user.id || "").toLowerCase();
            const name = user.FullName || user.fullName || user.label || user.name || "";
            const designation = Array.isArray(user.designations) && user.designations.length > 0 
                ? user.designations.map(d => d.DesignationName || d.designationName).filter(Boolean).join(", ")
                : (user.DesignationName || user.designationName || user.roleName || user.RoleName || "");
            return {
              id: uid,
              value: uid,
              label: name,
              badgeText: designation,
            };
          });
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key.startsWith("roleUser_")) {
        const roleUsers = predefinedFilter.users || [];
        options = roleUsers.map((u) => {
          const uid = String(u.userId || u.UserId || u.id || u.value || u.Id || "").toLowerCase();
          const name = u.userName || u.UserName || u.FullName || u.fullName || u.label || u.name || u.Email || "";
          const badge = Array.isArray(u.designations) && u.designations.length > 0 
              ? u.designations.map(d => d.DesignationName || d.designationName).filter(Boolean).join(", ")
              : (u.designation || u.Designation || u.Email || u.email || u.DesignationName || u.designationName || "");
          return {
            id: uid,
            value: uid,
            label: name,
            badgeText: badge,
          };
        });
        inputType = "multi-select";
      } else if (predefinedFilter.key === "groups") {
        if (groupsList && groupsList.length > 0) {
          options = groupsList.map((g) => {
            const gid = g.GroupId || g.groupId || g.id || g.value;
            const label = g.GroupName || g.groupName || g.name || g.label || "";
            return {
              id: gid,
              value: gid,
              label: label,
            };
          });
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key === "states") {
        if (statesList && statesList.length > 0) {
          options = statesList.map((s) => {
            const val = typeof s === "string" ? s : (s.StateCode || s.state || s.State || s.value || s.id || s.name);
            const label = typeof s === "string" ? s : (s.StateName || s.state || s.State || s.label || s.name || val);
            return {
              id: val,
              value: val,
              label: label,
            };
          });
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key === "status") {
        if (filterDropdowns.status && Array.isArray(filterDropdowns.status)) {
          options = filterDropdowns.status.map((option) => ({
            id: option.value || option.id,
            value: option.value || option.id,
            label: option.label,
          }));
        } else {
          options = [
            { id: "All", value: "All", label: "All Statuses" },
            { id: "Pending", value: "Pending", label: "Pending" },
            { id: "In Progress", value: "In Progress", label: "In Progress" },
            { id: "Completed", value: "Completed", label: "Completed" },
            { id: "Cancelled", value: "Cancelled", label: "Cancelled" },
          ];
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key === "hideAM") {
        inputType = "checkbox";
      } else if (
        ["createdDate", "modifiedDate"].includes(predefinedFilter.key)
      ) {
        inputType = "date-range";
      } else if (predefinedFilter.key === "dateRange") {
        // Special handling for date range filter
        inputType = "date-range";
      } else if (predefinedFilter.key === "priority") {
        if (
          filterDropdowns.PriorityId &&
          Array.isArray(filterDropdowns.PriorityId)
        ) {
          options = filterDropdowns.PriorityId.map((option) => ({
            id: option.value,
            value: option.value,
            label: option.label,
          }));
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key === "tags") {
        options = tags.map((tag) => ({
          id: tag.Id,
          value: tag.Id,
          label: tag.TagName || "",
        }));
        inputType = "multi-select";
      } else if (predefinedFilter.key === "referralSourceIds") {
        options = referralSources.map((source) => {
          const id =
            source.SourceId || source.sourceId || source.id || source.Id;
          const name = source.SourceName || source.sourceName || "";
          return {
            id: id,
            value: id,
            label: name,
          };
        });
        inputType = "multi-select";
      } else if (predefinedFilter.key === "interactionLevel") {
        if (
          filterDropdowns.InteractionLevelId &&
          Array.isArray(filterDropdowns.InteractionLevelId)
        ) {
          options = filterDropdowns.InteractionLevelId.map((option) => ({
            id: option.value,
            value: option.value,
            label: option.label,
          }));
        }
        inputType = "multi-select";
      } else if (predefinedFilter.key === "nationality") {
        if (
          filterDropdowns.Nationality &&
          Array.isArray(filterDropdowns.Nationality)
        ) {
          options = filterDropdowns.Nationality.map((option) => ({
            id: option.value,
            value: option.value,
            label: option.label,
          }));
        }
        inputType = "multi-select";
      } else {
        // Try to get dropdown options from advancedFilterFields if available
        if (advancedFilterFields && advancedFilterFields.length > 0) {
          const apiField = advancedFilterFields.find(
            (field) => field.fieldName === predefinedFilter.key,
          );
          if (apiField) {
            if (apiField.hasDropdownOptions && apiField.dropdownOptions) {
              options = apiField.dropdownOptions.map((option) => ({
                id: option.value,
                value: option.value,
                label: option.label,
              }));
            }
            // Use API field's inputType if different from predefined
            inputType = apiField.inputType || inputType;
          }
        }
      }

      const isSortedKey = !["leadScore", "stageSubstage"].includes(
        predefinedFilter.key,
      );
      const finalOptions = isSortedKey
        ? [...options].sort((a, b) =>
            (a.label || "").localeCompare(b.label || ""),
          )
        : options;

      definitionsMap[predefinedFilter.key] = {
        key: predefinedFilter.key,
        label: predefinedFilter.label,
        options: finalOptions,
        inputType: inputType,
        fieldDefinition: {
          fieldName: predefinedFilter.key,
          displayName: predefinedFilter.label,
          inputType: inputType,
          hasDropdownOptions: finalOptions.length > 0,
          dropdownOptions: finalOptions,
        },
      };
    });

    // Do NOT add any additional advanced filter fields - only predefined ones
    return definitionsMap;
  }, [stages, tags, referralSources, assignees, usersList, advancedFilterFields, filterDropdowns]);

  const filterDefinitionsMap = createFilterDefinitionsMap();

  // Get ordered and visible filters based on configuration
  const getVisibleFilters = () => {
    if (!filterConfig || !Array.isArray(filterConfig)) {
      return [];
    }
    return filterConfig
      .filter((config) => config.visible)
      .sort((a, b) => a.order - b.order)
      .map((config) => filterDefinitionsMap[config.key])
      .filter(Boolean); // Remove any undefined filters
  };

  const visibleFilters = getVisibleFilters();
  // const displayedFilters = showAllFilters
  //   ? visibleFilters
  //   : visibleFilters.slice(0, 4);
  // const moreFiltersCount = visibleFilters.length - 4;

  const displayedFilters = visibleFilters;
  const activeFiltersCount = getActiveFiltersCount();

  const toggleShowAllFilters = () => {
    setShowAllFilters(!showAllFilters);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleApplyAdvancedFilters = ({
    groupedFilters,
    appliedSavedFilters,
    quickFilters,
    shouldClearQuickFilters,
    keepOpen,
    clearQuickFilterKeys,
    isStartingEdit,
    ...rest
  }) => {
    // Store grouped filters and active templates in the filters state
    let newFilters = {
      ...localFilters,
      groupedFilters,
      appliedSavedFilters,
      ...rest,
    };

    if (quickFilters) {
      // Merge quick filters from the template
      newFilters = { ...newFilters, ...quickFilters };
    }

    if (rest.draftQuickFilters) {
      // Merge draft quick filters from the template being edited
      newFilters = { ...newFilters, ...rest.draftQuickFilters };
    }

    if (shouldClearQuickFilters) {
      // Clear all filters except the basic structure and persistent settings
      newFilters = {
        selectedColumns: localFilters.selectedColumns,
        applicantTab: localFilters.applicantTab,
        leadview: localFilters.leadview,
        groupedFilters,
        appliedSavedFilters,
        ...rest,
      };

      if (quickFilters) {
        newFilters = { ...newFilters, ...quickFilters };
      }
      if (rest.draftQuickFilters) {
        newFilters = { ...newFilters, ...rest.draftQuickFilters };
      }
    }

    if (clearQuickFilterKeys && Array.isArray(clearQuickFilterKeys)) {
      clearQuickFilterKeys.forEach((key) => {
        delete newFilters[key];
        if (key === "stageSubstage") {
          delete newFilters.stageId;
          delete newFilters.subStageId;
          delete newFilters.stageFilters;
        }
      });
    }

    // If keepOpen is true (editing a saved filter), we only update the local state
    // to reflect values in the dropdowns without triggering a parent list refresh.
    // UNLESS shouldClearQuickFilters is true, in which case we want to sync the global state.
    setLocalFilters(translateApiToUiFilters(newFilters));
    
    if (!isStartingEdit) {
      const apiFilters = mapUiFiltersToApiFilters(newFilters);
      onFilterChange?.(apiFilters);
    }

    // Close unless explicitly keeping open
    if (!keepOpen) {
      onCloseFilters?.(); // Close the on-page filter section
      onClose?.(); // Close the side drawer filter section (if inside one)
    }
  };

  // Fetch stages and tags on component mount (using eager-loaded cache if available)
  useEffect(() => {
    const fetchStages = async () => {
      if (cachedStages) {
        setStages(cachedStages);
        setLoadingStages(false);
        return;
      }
      setLoadingStages(true);
      try {
        eagerLoadStages();
        const data = await stagesPromise;
        if (data) {
          setStages(data);
        }
      } catch (error) {
        console.error("Error fetching stages:", error);
      } finally {
        setLoadingStages(false);
      }
    };

    const fetchTags = async () => {
      if (cachedTags) {
        setTags(cachedTags);
        setLoadingTags(false);
        return;
      }
      setLoadingStages(true);
      try {
        eagerLoadTags();
        const data = await tagsPromise;
        if (data) {
          setTags(data);
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      } finally {
        setLoadingTags(false);
      }
    };

    const fetchReferralSources = async () => {
      if (cachedReferralSources) {
        setReferralSources(cachedReferralSources);
        setLoadingReferralSources(false);
        return;
      }
      setLoadingReferralSources(true);
      try {
        eagerLoadReferralSources();
        const data = await referralSourcesPromise;
        if (data) {
          setReferralSources(data);
        }
      } catch (error) {
        console.error("Error fetching referral sources:", error);
      } finally {
        setLoadingReferralSources(false);
      }
    };

    const fetchAssignees = async () => {
      if (cachedAssignees) {
        setAssignees(cachedAssignees);
        setLoadingAssignees(false);
        return;
      }
      setLoadingAssignees(true);
      try {
        eagerLoadAssignees();
        const data = await assigneesPromise;
        if (data) {
          setAssignees(data);
        }
      } catch (error) {
        console.error("Error fetching assignees:", error);
      } finally {
        setLoadingAssignees(false);
      }
    };

    const fetchUsers = async () => {
      if (cachedUsers) {
        setUsersList(cachedUsers);
        setLoadingUsers(false);
        return;
      }
      setLoadingUsers(true);
      try {
        eagerLoadUsers();
        const data = await usersPromise;
        if (data) {
          setUsersList(data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoadingUsers(false);
      }
    };

    const fetchGroups = async () => {
      if (cachedGroups) {
        setGroupsList(cachedGroups);
        setLoadingGroups(false);
        return;
      }
      setLoadingGroups(true);
      try {
        eagerLoadGroups();
        const data = await groupsPromise;
        if (data) setGroupsList(data);
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setLoadingGroups(false);
      }
    };

    const fetchStates = async () => {
      if (cachedStates) {
        setStatesList(cachedStates);
        setLoadingStates(false);
        return;
      }
      setLoadingStates(true);
      try {
        eagerLoadStates();
        const data = await statesPromise;
        if (data) setStatesList(data);
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoadingStates(false);
      }
    };

    const fetchRolesWithUsers = async () => {
      if (cachedRolesWithUsers) {
        setRolesWithUsers(cachedRolesWithUsers);
        setLoadingRolesWithUsers(false);
        return;
      }
      setLoadingRolesWithUsers(true);
      try {
        eagerLoadRolesWithUsers();
        const data = await rolesWithUsersPromise;
        if (data) setRolesWithUsers(data);
      } catch (error) {
        console.error("Error fetching roles with users:", error);
      } finally {
        setLoadingRolesWithUsers(false);
      }
    };

    fetchStages();
    fetchTags();
    fetchReferralSources();
    fetchAssignees();
    fetchUsers();
    fetchGroups();
    fetchStates();
    fetchRolesWithUsers();
  }, []);

  const selectedStageOrSubstage =
    localFilters.stageFilters ||
    localFilters.subStageId ||
    localFilters.stageId ||
    localFilters.stageSubstage; // fallback

  // Helper function to get combined stage and substage values for multi-select
  // Reconstructs the flat list of selected IDs from the new stageFilters structure
  const getStageOrSubstageValues = () => {
    // Prefer the new stageFilters format
    if (localFilters.stageFilters && Array.isArray(localFilters.stageFilters)) {
      return flattenStageFilters(localFilters.stageFilters);
    }

    // Fallback: old flat format
    const stageIds = Array.isArray(localFilters.stageId)
      ? localFilters.stageId
      : localFilters.stageId
        ? [localFilters.stageId]
        : [];
    const subStageIds = Array.isArray(localFilters.subStageId)
      ? localFilters.subStageId
      : localFilters.subStageId
        ? [localFilters.subStageId]
        : [];
    const combinedValues = [...new Set([...stageIds, ...subStageIds])];
    if (combinedValues.length > 0) return combinedValues;

    // No explicit selection yet — show what's visible by default (everything
    // except Lost/job stages), without writing it into the filter state.
    return defaultStageOrSubstageIds;
  };

  return (
    <div
      id="lead-filters-container"
      className={clsx("relative flex h-full flex-col", className)}
    >
      {/* Tabs at the very top */}
      <div className="sticky top-0 z-30 mb-3 flex border-b border-slate-200/60 bg-white px-1 dark:border-slate-700/60 dark:bg-slate-900">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab("filter")}
            className={clsx(
              "relative flex items-center gap-2 border-b-[2.5px] py-3 text-[13px] font-semibold transition-all",
              activeTab === "filter"
                ? "border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
            )}
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={() => setActiveTab("saved")}
            className={clsx(
              "relative flex items-center gap-2 border-b-[2.5px] py-3 text-[13px] font-semibold transition-all",
              activeTab === "saved"
                ? "border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
            )}
          >
            <BookmarkIcon className="h-4 w-4" />
            Saved Templates{" "}
            {savedCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {savedCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Combined Filters Container */}
      <div className="flex-1 space-y-4 overflow-y-auto px-4 pr-1 pb-4">
        {activeTab === "filter" && (
          <>
            {/* Quick Filters Section */}
            <div className="relative rounded-2xl border border-slate-200/60 bg-white/50 shadow-sm backdrop-blur-sm transition-all hover:shadow-md dark:border-slate-700/60 dark:bg-slate-800/50">
              {/* Section Accent */}
              <div className="from-primary-400 to-primary-600 absolute inset-x-0 top-0 h-[3px] rounded-t-2xl bg-gradient-to-r opacity-80" />

              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400 flex h-9 w-9 items-center justify-center rounded-xl shadow-inner">
                      <Cog6ToothIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-[14px] font-semibold tracking-tight text-slate-900 dark:text-white">
                        Quick Filters
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Filters Grid */}
                <div
                  className={clsx(
                    "transition-all duration-300 ease-in-out",
                    isQuickFiltersOpen
                      ? "mt-4 max-h-[1200px] overflow-visible opacity-100"
                      : "max-h-0 overflow-hidden opacity-0",
                  )}
                >
                  {/* ── Regular (non-role) filters ── */}
                  {(() => {
                    const nonRoleFilters = displayedFilters.filter(
                      (f) => !f.key.startsWith("roleUser_") && f.key !== "users"
                    );
                    const roleFilters = displayedFilters.filter(
                      (f) => f.key.startsWith("roleUser_") || f.key === "users"
                    );

                    // Count how many role filters have a selection
                    const activeRoleCount = roleFilters.filter((f) => {
                      const v = localFilters[f.key];
                      return Array.isArray(v) ? v.length > 0 : !!v;
                    }).length;

                    const renderFilterInput = (filter) => (
                      <div
                        key={filter.key}
                        className={clsx(
                          "flex flex-col gap-1.5",
                          filter.key === "dateRange" && "sm:col-span-2",
                          activeDropdown === filter.key ? "relative z-20" : "relative z-0",
                        )}
                      >
                        {filter.inputType !== "checkbox" ? (
                          <label
                            className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase dark:text-gray-400 truncate"
                            title={filter.label}
                          >
                            {filter.label}
                          </label>
                        ) : (
                          <label className="block text-[11px] font-bold tracking-wider text-transparent select-none invisible pointer-events-none truncate">
                            {filter.label}
                          </label>
                        )}

                        {filter.inputType === "checkbox" ? (
                          <div className="flex items-center h-[34px]">
                            <Checkbox
                              label={filter.label}
                              checked={!!localFilters[filter.key]}
                              onChange={(e) => handleFilterSelect(filter.key, e.target.checked)}
                              color="primary"
                              classNames={{
                                labelText: "text-sm font-medium text-slate-700 transition-colors group-hover:text-slate-900 dark:text-slate-300 dark:group-hover:text-white",
                                label: "group cursor-pointer m-0 !pl-0"
                              }}
                              className="!ml-0"
                            />
                          </div>
                        ) : filter.inputType === "date" ? (
                          <DatePicker
                            value={localFilters[filter.key] || ""}
                            onChange={(dates, dateStr) => handleFilterSelect(filter.key, dateStr)}
                            options={{ altInput: true, altFormat: "d/m/Y", dateFormat: "d-m-Y" }}
                            className="w-full"
                          />
                        ) : filter.inputType === "date-range" ? (
                          <DateRangePickerFilter
                            filterKey={filter.key}
                            label={null}
                            value={localFilters[filter.key]}
                            onChange={(value) => handleFilterSelect(filter.key, value)}
                            onClear={() => handleClearFilter(filter.key)}
                            isOpen={activeDropdown === filter.key}
                            onToggle={setActiveDropdown}
                          />
                        ) : filter.inputType === "multi-select-hierarchy" ? (
                          <MultiSelectHierarchicalFilterDropdown
                            filterKey={filter.key}
                            options={filter.options}
                            label={null}
                            selectedValues={getStageOrSubstageValues()}
                            onSelect={handleFilterSelect}
                            onClear={handleClearFilter}
                            disabled={loadingStages}
                            isOpen={activeDropdown === filter.key}
                            onToggle={setActiveDropdown}
                            usePortal={true}
                          />
                        ) : (
                          <MultiSelectFilterDropdown
                            filterKey={filter.key}
                            options={filter.options}
                            label={null}
                            selectedValues={localFilters[filter.key] ?? []}
                            onSelect={handleFilterSelect}
                            onClear={handleClearFilter}
                            disabled={
                              filter.key === "tags" ? loadingTags
                              : filter.key === "referralSourceIds" ? loadingReferralSources
                              : filter.key === "assignedTo" ? loadingAssignees
                              : filter.key === "groups" ? loadingGroups
                              : filter.key === "states" ? loadingStates
                              : filter.key.startsWith("roleUser_") ? loadingRolesWithUsers
                              : (loadingStages && (filter.key === "stageId" || filter.key === "subStageId"))
                            }
                            isOpen={activeDropdown === filter.key}
                            onToggle={setActiveDropdown}
                            usePortal={true}
                          />
                        )}
                      </div>
                    );

                    return (
                      <>
                        {/* Non-role quick filters */}
                        <div className="grid grid-cols-1 gap-4 pb-2 sm:grid-cols-3">
                          {nonRoleFilters.map(renderFilterInput)}
                        </div>

                        {/* ── Role / Team filters collapsible sub-section ── */}
                        {roleFilters.length > 0 && (
                          <div className="mt-3 rounded-xl border border-slate-200/80 dark:border-slate-700/60 overflow-hidden">
                            {/* Header toggle */}
                            <button
                              type="button"
                              onClick={() => setIsRoleFiltersOpen((prev) => !prev)}
                              className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-700/60 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <UserGroupIcon className="h-4 w-4 text-slate-500 dark:text-slate-400 shrink-0" />
                                <span className="text-[12px] font-semibold text-slate-600 dark:text-slate-300 tracking-wide">
                                  Designations
                                </span>
                                {activeRoleCount > 0 && (
                                  <span className="inline-flex items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 px-2 py-0.5 text-[10px] font-bold">
                                    {activeRoleCount} active
                                  </span>
                                )}
                                {loadingRolesWithUsers && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                    </svg>
                                    Loading…
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                  {roleFilters.length} {roleFilters.length === 1 ? "designation" : "designations"}
                                </span>
                                <ChevronDownIcon
                                  className={clsx(
                                    "h-4 w-4 text-slate-400 transition-transform duration-200",
                                    isRoleFiltersOpen && "rotate-180"
                                  )}
                                />
                              </div>
                            </button>

                            {/* Collapsible body */}
                            <div
                              className={clsx(
                                "transition-all duration-300 ease-in-out overflow-hidden",
                                isRoleFiltersOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                              )}
                            >
                              <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                {roleFilters.map(renderFilterInput)}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
              </div>
                </div>
              </div>
          </>
        )}

        {/* Advanced Filters Section */}
        <div className="relative mt-6 rounded-2xl border border-slate-200/60 bg-white/50 shadow-sm backdrop-blur-sm transition-all hover:shadow-md dark:border-slate-700/60 dark:bg-slate-800/50">
          {/* Section Accent */}
          <div className="from-primary-400 to-primary-600 absolute inset-x-0 top-0 h-[3px] rounded-t-2xl bg-gradient-to-r opacity-80" />

          <div className="p-4">
            {activeTab === "filter" && (
              <div className="mb-4 flex items-center gap-3">
                <div className="bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400 flex h-9 w-9 items-center justify-center rounded-xl shadow-inner">
                  <AdjustmentsHorizontalIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-[14px] font-semibold tracking-tight text-slate-900 dark:text-white">
                    Advanced Filters
                  </h3>
                </div>
              </div>
            )}

            <AdvancedFilters
              onApplyFilters={handleApplyAdvancedFilters}
              currentFilters={localFilters}
              appliedFilters={filters}
              advancedFilterFields={advancedFilterFields}
              supportedOperators={supportedOperators}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onSavedCountChange={setSavedCount}
              tagsData={tags}
              entityType={entityType}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
